import { Controller } from "stimulus";

// Expands the new-comment editor into a centered large modal so longer replies
// are comfortable to write, and collapses it back. It only toggles Tailwind
// classes on the existing elements (container becomes the backdrop, form becomes
// the modal panel, editor grows) — the Tiptap editor DOM is never moved, so the
// form submits exactly as it did before. Escape or a click on the backdrop
// collapses it; the body scroll is locked while open.
//
// The Tiptap editor host (.tr-editor) is created by the tiptap-editor controller
// as a sibling of the hidden <textarea>, so it is resolved at toggle time rather
// than via a Stimulus target.
export default class extends Controller {
  static targets = ["form", "label", "enterIcon", "exitIcon"];
  static classes = ["container", "form", "editor"];

  connect() {
    this.expanded = false;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    this.collapse();
  }

  toggle() {
    if (this.expanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    this.element.classList.add(...this.containerClasses);
    this.formTarget.classList.add(...this.formClasses);
    const host = this.editorHost;
    if (host) {
      host.classList.add(...this.editorClasses, "flex", "flex-col");
      const surface = host.querySelector(".ProseMirror");
      if (surface) surface.classList.add("flex-1");
    }
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.handleKeydown);
    this.reflectState(true);
    this.focusEditor();
    this.expanded = true;
  }

  collapse() {
    if (!this.expanded) return;

    this.element.classList.remove(...this.containerClasses);
    this.formTarget.classList.remove(...this.formClasses);
    const host = this.editorHost;
    if (host) {
      host.classList.remove(...this.editorClasses, "flex", "flex-col");
      const surface = host.querySelector(".ProseMirror");
      if (surface) surface.classList.remove("flex-1");
    }
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.handleKeydown);
    this.reflectState(false);
    this.expanded = false;
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.collapse();
  }

  // Closes the modal only when the backdrop itself is clicked, not its content.
  backdropClose(event) {
    if (this.expanded && event.target === this.element) this.collapse();
  }

  reflectState(isExpanded) {
    if (this.hasLabelTarget) this.labelTarget.textContent = isExpanded ? "Recolher" : "Ampliar";
    if (this.hasEnterIconTarget) this.enterIconTarget.classList.toggle("hidden", isExpanded);
    if (this.hasExitIconTarget) this.exitIconTarget.classList.toggle("hidden", !isExpanded);
  }

  get editorHost() {
    return this.element.querySelector(".tr-editor");
  }

  focusEditor() {
    const surface = this.element.querySelector(".tr-editor .ProseMirror");
    if (surface) surface.focus();
  }
}
